import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useI18n, type Key } from '../i18n';
import { useShopEntry } from '../session';
import { Halftone, Petal, Plate, Splat } from '../ui/chrome';
import { AppleGlyph, OpenBookGlyph, PencilGlyph, TownGlyph } from '../ui/glyphs';
import { COPY, type FeatureKey } from './features-content';
import { PreregForm } from '../ui/Prereg';

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL;
const FEATURES: { key: 'f1' | 'f2' | 'f3'; page: FeatureKey; art: string; icon: ReactNode; tone: string }[] = [
  { key: 'f1', page: 'choices', art: '/art/strip_1.webp', icon: <PencilGlyph />, tone: 'var(--sakura)' },
  { key: 'f2', page: 'neighbours', art: '/art/strip_2.webp', icon: <TownGlyph />, tone: 'var(--lime)' },
  { key: 'f3', page: 'manga', art: '/art/strip_3.webp', icon: <OpenBookGlyph />, tone: 'var(--lime)' },
];

export function LandingPage() {
  const { t, tx } = useI18n();
  const shopEntry = useShopEntry();
  return (
    <div className="landing">
      <section className="hero">
        <div className="hero-field" aria-hidden />
        <Splat n={1} style={{ left: -80, top: 60, width: 250, zIndex: 2, wide: true }} />
        <Halftone style={{ left: 0, top: 30, width: 190, height: 300, zIndex: 1 }} />
        <Splat n={2} style={{ right: -50, top: -20, width: 180, zIndex: 4, wide: true }} />
        <Petal n={1} style={{ left: 590, top: 70, width: 74, zIndex: 4, wide: true }} />
        <Petal n={2} style={{ left: 24, top: 430, width: 64, zIndex: 3, wide: true }} />
        <div className="wrap">
          <div className="hero-copy">
            <h1>
              <span>{t('landing.title1')}</span>
              <span>{t('landing.title2')}</span>
            </h1>
            <p className="lead">
              <span>{t('landing.lead1')}</span> <span>{t('landing.lead2')}</span>
            </p>
            {/* Before the App Store listing: sign up to hear when it launches (and get the launch gift). */}
            {APP_STORE_URL ? null : (
              <div className="hero-prereg">
                <span className="hero-prereg-tag">{t('landing.appStoreSoon')}</span>
                <PreregForm source="landing" compact />
              </div>
            )}
            <div className="hero-actions">
              {APP_STORE_URL ? (
                <a className="store-badge" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                  <AppleGlyph />
                  <span>
                    <b>App Store</b>
                    <small>{t('landing.appStore')}</small>
                  </span>
                </a>
              ) : null}
              {shopEntry ? (
                <Link className="btn btn-pink" to="/shop">
                  {t('landing.toShop')}
                </Link>
              ) : null}
            </div>
          </div>
        </div>
        <img className="hero-lineup" src="/art/lineup.webp" alt={t('landing.cast')} width={1400} height={869} />
      </section>
      <section className="features">
        <div className="wrap">
          {FEATURES.map((f) => (
            <Link key={f.key} to={`/features/${f.page}`} className="card feature">
              <div className="feature-copy">
                <Plate as="h2">{t(`landing.${f.key}.title` as Key)}</Plate>
                <div className="feature-body">
                  <span className="feature-icon" style={{ background: f.tone }}>
                    {f.icon}
                  </span>
                  <p>{t(`landing.${f.key}.body` as Key)}</p>
                </div>
                <div className="rule" style={{ background: f.tone }} />
              </div>
              <div className="feature-art" style={{ backgroundImage: `url(${f.art})` }} role="img" aria-label="" />
              <span className="feature-more">{tx(COPY.more)} →</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
